import * as React from "react";
import { useApiService, useTl } from "../../../../hooks";
import { ChartSignatureHistoryApi, ChartSignaturesHistorySearch, ChartSignaturesHistoryDto } from "../../../../api";
import { useEventsContext, useDialog } from "../../../../contexts";
import { IDataTableColumn, ISearchCriteriaValues, showSuccess, showError, FieldSet } from "nsitools-react";
import { ETLCodes } from "../../../../locales";
import { SearchTablePage, DeleteButton } from "../../../../components";
import { Dialog, Classes, Colors } from "@blueprintjs/core";
import { PersonBreederChartSavePanel } from "./PersonBreederChartSavePanel";

export interface IPersonBreederChartProps {
  personId: number;
}

export const PersonBreederChart: React.FunctionComponent<IPersonBreederChartProps> = ({ personId }) => {
  const api = useApiService(ChartSignatureHistoryApi);
  const { t } = useTl();
  const { dispatchEvent } = useEventsContext();
  const [isDialogOpen, setisDialogOpen] = React.useState<boolean>(false);
  const { showDialogPromise } = useDialog();

  const deleteFunction = React.useCallback(
    async (chartId: number) => {
      try {
        const dialogResult = await showDialogPromise({
          message: t(ETLCodes.DeleteConfirmationMessage),
          title: t(ETLCodes.Warning),
        });
        if (dialogResult !== "yes") return;
        await api.deleteChartSignatureHistory(chartId);
        showSuccess(t(ETLCodes.DeleteSuccess));
        dispatchEvent("RefreshSearchTables", 0);
      } catch {
        showError(t(ETLCodes.Error));
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );
  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "_action",
        autoFitContent: true,
        render: (row: ChartSignaturesHistoryDto) => (
          <DeleteButton onDelete={() => deleteFunction(row.chartSignaturesHistoryId)} />
        ),
      },
      {
        header: () => t(ETLCodes.DateFrom),
        fieldName: "dateFrom",
      },
      {
        header: () => t(ETLCodes.DateTo),
        fieldName: "dateTo",
      },
    ],
    [deleteFunction, t]
  );

  const searchCallback = React.useCallback(
    (sObj: ISearchCriteriaValues) =>
      api.baseSearch(ChartSignaturesHistorySearch.fromJS({ ...sObj, personId: personId })),
    [api, personId]
  );
  const exportCallback = React.useCallback(
    (sObj: ISearchCriteriaValues) =>
      api.exportXlsFile(ChartSignaturesHistorySearch.fromJS({ ...sObj, personId: personId })),
    [api, personId]
  );
  const addFunc = React.useCallback(() => {
    setisDialogOpen(true);
  }, []);

  return (
    <>
      <FieldSet title={t(ETLCodes.Chart)}>
        <SearchTablePage
          title={t(ETLCodes.Chart)}
          columns={columns}
          searchFunction={searchCallback}
          exportFunction={exportCallback}
          sortKeys={{ dateFrom: "ASC" }}
          withCard={false}
          enableFilter={false}
          addFunc={addFunc}
          saveCriterias={false}
          criteriaSavePrefix={"chart"}
        />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.ChartSignatureHistoryAdd)}
        isOpen={isDialogOpen}
        onClose={() => setisDialogOpen(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1000, height: 200, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 100 }}>
          <PersonBreederChartSavePanel
            onSaved={() => {
              setisDialogOpen(false);
              dispatchEvent("RefreshSearchTables", 0);
            }}
            personId={personId}
          />
        </div>
      </Dialog>
    </>
  );
};
